import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Grid, Stack, Typography, Alert, List,
  ListItem, ListItemText, Chip, TextField,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestoreIcon from '@mui/icons-material/Restore';
import UploadIcon from '@mui/icons-material/Upload';
import { useNavigate } from 'react-router-dom';
import { SectionHeader } from '../components/ui';
import {
  getCvProfiles,
  importSkillsFromCv,
  importCoursesFromCv,
  syncCompletedCertificationsToCv,
} from '../utils/cvIntegration';
import { getCompletedItems, collectGrowthBackups, countRouteItems } from '../utils/learningResources';
import { useGrowthPlannerStore } from '../store/growthPlannerStore';

export default function IntegrationPage() {
  const navigate = useNavigate();
  const routes = useGrowthPlannerStore((s) => s.routes);
  const learningResources = useGrowthPlannerStore((s) => s.learningResources);
  const importSkills = useGrowthPlannerStore((s) => s.importSkills);
  const importGrowthData = useGrowthPlannerStore((s) => s.importGrowthData);
  const recoverFromBackups = useGrowthPlannerStore((s) => s.recoverFromBackups);
  const [msg, setMsg] = useState('');
  const [importJson, setImportJson] = useState('');
  const [profiles,setProfiles] = useState([]);
  useEffect(()=>{getCvProfiles().then(setProfiles).catch(e=>setMsg(e.response?.data?.message||e.message));},[msg]);

  const completedItems = getCompletedItems(routes, learningResources);
  const currentItems = countRouteItems(routes);
  const backups = collectGrowthBackups();

  const handleSyncCerts = async () => {
    try {
    const count = await syncCompletedCertificationsToCv(completedItems);
    setMsg(`Sincronizados ${count} cursos/certificaciones al CV activo.`);
    } catch(e) { setMsg(e.response?.data?.message||e.message); }
  };

  const handleImportAll = async () => {
    try {
    const skills = await importSkillsFromCv();
    importSkills(skills);
    setMsg(`Importadas ${skills.length} skills. ${(await importCoursesFromCv()).length} cursos detectados en CV.`);
    } catch(e) { setMsg(e.response?.data?.message||e.message); }
  };

  const handleRecoverBackups = () => {
    recoverFromBackups();
    const after = countRouteItems(useGrowthPlannerStore.getState().routes);
    if (after > currentItems) {
      setMsg(`Recuperados ${after - currentItems} item(s) desde backups locales (${after} en total).`);
    } else if (after > 0) {
      setMsg(`Datos revisados. Tienes ${after} item(s) en tus rutas.`);
    } else {
      setMsg('No se encontraron items en backups locales. Prueba importar un JSON antiguo abajo.');
    }
  };

  const handleImportJson = () => {
    const result = importGrowthData(importJson);
    if (!result.ok) {
      setMsg(result.error || 'No se pudo importar el JSON.');
      return;
    }
    setImportJson('');
    setMsg(`Importación completada: ${result.items} item(s) en rutas${result.mergedGroups ? `, ${result.mergedGroups} grupo(s) duplicados unificados` : ''}.`);
  };

  return (
    <Box>
      <SectionHeader
        title="Integración con CV"
        subtitle="Reutiliza y sincroniza datos entre Growth Planner y Constructor de Currículum"
      />

      {msg && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setMsg('')}>{msg}</Alert>}

      {currentItems === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Tu ruta existe pero no tiene cursos/certificaciones guardados ({currentItems} items).
          Esto suele pasar si la nube sobrescribió datos locales. Usa &quot;Recuperar backups&quot; o pega abajo un JSON antiguo con items en las etapas.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Recuperar plan de formación</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Restaura items del formato anterior (inline en etapas), fusiona duplicados por título+URL
                y conserva placements en cada etapa. Backups locales detectados: {backups.length}.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                <Button variant="contained" startIcon={<RestoreIcon />} onClick={handleRecoverBackups}>
                  Recuperar desde backups locales
                </Button>
              </Stack>
              <TextField
                label="Pegar JSON de respaldo (formato antiguo o nuevo)"
                multiline
                minRows={6}
                fullWidth
                value={importJson}
                onChange={(e) => setImportJson(e.target.value)}
                placeholder='{"routes":[{"stages":[{"items":[{"title":"Google BI","url":"..."}]}]}]}'
                sx={{ mb: 1 }}
              />
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={handleImportJson}
                disabled={!importJson.trim()}
              >
                Importar y fusionar JSON
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Sincronización</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Importa skills desde tu CV activo o exporta certificaciones completadas.
              </Typography>
              <Stack spacing={1}>
                <Button variant="outlined" startIcon={<SyncIcon />} onClick={handleImportAll} fullWidth>
                  Importar skills del CV
                </Button>
                <Button variant="outlined" startIcon={<SyncIcon />} onClick={handleSyncCerts} fullWidth>
                  Exportar certificaciones completadas al CV
                </Button>
                <Button
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => navigate('/apps/cv-builder')}
                  fullWidth
                >
                  Abrir Constructor de Currículum
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Perfiles profesionales (CV)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Crea distintos CVs según tu carrera objetivo. Desde cada ruta puedes generar un perfil automáticamente.
              </Typography>
              {profiles.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No hay perfiles CV aún.</Typography>
              ) : (
                <List dense disablePadding>
                  {profiles.map((p) => {
                    const linkedRoute = routes.find((r) => r.profileId === p.id || p.linkedRouteId === r.id);
                    return (
                      <ListItem key={p.id} disableGutters sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={p.name}
                          secondary={p.targetRole || 'Sin objetivo definido'}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                        {linkedRoute && <Chip label={linkedRoute.name} size="small" variant="outlined" />}
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Flujo recomendado</Typography>
              <Stack spacing={1}>
                <Typography variant="body2">1. Crea una ruta profesional con etapas y recursos</Typography>
                <Typography variant="body2">2. Genera un perfil CV desde la ruta (botón &quot;Crear CV&quot;)</Typography>
                <Typography variant="body2">3. Importa skills compartidas o sincroniza certificaciones completadas</Typography>
                <Typography variant="body2">4. Edita el CV específico en el Constructor con el perfil activo</Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
